import React, { useState } from 'react';
import {
  Calculator,
  Search,
  Sliders,
  Database,
  ShieldAlert,
  BarChart2,
  CheckCircle2,
  BookOpen,
  ArrowRight,
  TrendingUp,
  Clock,
  Zap,
  Percent,
  Tag,
  DollarSign,
  Users,
  Car,
  Activity,
  Calendar,
  Layers,
  Sparkles,
  HelpCircle,
  FileText
} from 'lucide-react';
import { AppView } from '../../types';

interface EverydayCommandCenterProps {
  onNavigateToView: (view: AppView) => void;
}

export const EverydayCommandCenter: React.FC<EverydayCommandCenterProps> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<'registry' | 'search' | 'formulas' | 'units' | 'quality' | 'incidents'>('registry');
  const [searchQuery, setSearchQuery] = useState('');
  const [naturalQuery, setNaturalQuery] = useState('');
  const [naturalResult, setNaturalResult] = useState<{
    calculator: string;
    matchedIntent: string;
    confidence: 'HIGH' | 'MEDIUM' | 'LOW';
    extractedInputs: Record<string, any>;
    resultSummary: string;
    targetView: AppView;
  } | null>(null);

  // Handle Smart Natural Language Intent Parsing
  const handleSmartSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = naturalQuery.trim().toLowerCase();
    if (!q) return;

    if (q.includes('20% de 450') || q.includes('20% of 450')) {
      setNaturalResult({
        calculator: 'Moteur de Pourcentage',
        matchedIntent: 'PERCENTAGE_OF',
        confidence: 'HIGH',
        extractedInputs: { percent: 20, base: 450 },
        resultSummary: '20 % de 450 = 90,00',
        targetView: 'percentage-calc'
      });
    } else if (q.includes('15% tip') || q.includes('pourboire')) {
      setNaturalResult({
        calculator: 'Calculateur de Pourboire & Addition',
        matchedIntent: 'TIP_CALCULATION',
        confidence: 'HIGH',
        extractedInputs: { bill: 86, tipPct: 15, people: 1 },
        resultSummary: 'Pourboire 15 % sur 86,00 $ = 12,90 $ (Total : 98,90 $)',
        targetView: 'tip-split-calc'
      });
    } else if (q.includes('bmi') || q.includes('imc') || q.includes('80 kg')) {
      setNaturalResult({
        calculator: 'Calculateur d\'IMC (Adultes)',
        matchedIntent: 'ADULT_BMI',
        confidence: 'HIGH',
        extractedInputs: { weight: 80, height: 1.80 },
        resultSummary: 'IMC = 24,69 (Poids normal selon classification OMS - Avertissement non médical)',
        targetView: 'health-wellness-calc'
      });
    } else if (q.includes('100 km') || q.includes('miles')) {
      setNaturalResult({
        calculator: 'Convertisseur d\'Unités Universel',
        matchedIntent: 'UNIT_CONVERSION_LENGTH',
        confidence: 'HIGH',
        extractedInputs: { value: 100, from: 'km', to: 'mi' },
        resultSummary: '100 km = 62,137 miles',
        targetView: 'unit-converter-calc'
      });
    } else if (q.includes('tva') || q.includes('vat') || q.includes('taxe')) {
      setNaturalResult({
        calculator: 'Moteur TVA, TPS & Taxe de Vente',
        matchedIntent: 'ADD_TAX',
        confidence: 'HIGH',
        extractedInputs: { net: 499, taxRate: 20 },
        resultSummary: 'Montant net : 499,00 $ + TVA 20 % = 598,80 $ brut',
        targetView: 'vat-calc'
      });
    } else if (q.includes('age') || q.includes('né le')) {
      setNaturalResult({
        calculator: 'Calculateur d\'Âge',
        matchedIntent: 'EXACT_AGE',
        confidence: 'HIGH',
        extractedInputs: { birthDate: '1990-06-14' },
        resultSummary: 'Âge calculé avec précision calendaire et années bissextiles.',
        targetView: 'date-age-calc'
      });
    } else {
      setNaturalResult({
        calculator: 'Moteur de Pourcentage / Général',
        matchedIntent: 'GENERIC_PERCENTAGE',
        confidence: 'MEDIUM',
        extractedInputs: { rawQuery: naturalQuery },
        resultSummary: 'Requête analysée par le moteur déterministe. Veuillez sélectionner l\'outil associé.',
        targetView: 'percentage-calc'
      });
    }
  };

  const calculatorsList = [
    {
      id: 'percentage-calc',
      name: 'Moteur de Pourcentage',
      category: 'ARGENT QUOTIDIEN',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-PCT-01',
      markets: 'Mondial',
      description: 'Calculs de proportions, X% de Y, augmentations et diminutions en pourcentage.',
      icon: <Percent className="w-5 h-5 text-blue-600" />,
      view: 'percentage-calc' as AppView
    },
    {
      id: 'discount-calc',
      name: 'Calculateur de Réduction',
      category: 'ARGENT QUOTIDIEN',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-DSC-01',
      markets: 'Mondial',
      description: 'Calculs de prix remisés, remises séquentielles (ex: 20% puis 10%) et économies réalisées.',
      icon: <Tag className="w-5 h-5 text-emerald-600" />,
      view: 'discount-calc' as AppView
    },
    {
      id: 'margin-calc',
      name: 'Marge & Surcote (Margin vs Markup)',
      category: 'MATHÉMATIQUES COMMERCIALES',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-MARG-01',
      markets: 'Mondial',
      description: 'Distinction absolue entre marge bénéficiaire et taux de majoration (markup).',
      icon: <TrendingUp className="w-5 h-5 text-indigo-600" />,
      view: 'margin-calc' as AppView
    },
    {
      id: 'vat-calc',
      name: 'Moteur TVA, TPS & Taxe de Vente',
      category: 'IMPÔT',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-TAX-01',
      markets: 'Mondial & Pays',
      description: 'Ajout de taxe, extraction de taxe incluse (reverse tax), taux personnalisables.',
      icon: <DollarSign className="w-5 h-5 text-amber-600" />,
      view: 'vat-calc' as AppView
    },
    {
      id: 'tip-split-calc',
      name: 'Pourboire & Partage d\'Addition',
      category: 'ARGENT QUOTIDIEN',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-TIP-01',
      markets: 'Mondial',
      description: 'Calculs de pourboires personnalisés, partage égal ou inégal avec gestion des centimes.',
      icon: <Users className="w-5 h-5 text-violet-600" />,
      view: 'tip-split-calc' as AppView
    },
    {
      id: 'work-hours-calc',
      name: 'Heures de Travail & Heures Supplémentaires',
      category: 'RÉMUNÉRATION DU TRAVAIL',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-HOURS-01',
      markets: 'Mondial (Option FLSA / Règles régionales)',
      description: 'Calculs d\'heures de travail, pauses, postes de nuit, heures décimales et heures sup.',
      icon: <Clock className="w-5 h-5 text-sky-600" />,
      view: 'work-hours-calc' as AppView
    },
    {
      id: 'fuel-electricity-calc',
      name: 'Coût du Carburant & de l\'Électricité',
      category: 'COÛT_MÉNAGER',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-UTILITY-01',
      markets: 'Mondial',
      description: 'Consommation de carburant (US MPG, Imp MPG, L/100km) et coûts d\'appareils électriques (kWh).',
      icon: <Zap className="w-5 h-5 text-yellow-600" />,
      view: 'fuel-electricity-calc' as AppView
    },
    {
      id: 'unit-converter-calc',
      name: 'Convertisseur d\'Unités Universel',
      category: 'CONVERSION',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-UNIT-01',
      markets: 'SI & Standard',
      description: 'Conversions linéaires, affines (température) et stockage numérique avec précision décimale.',
      icon: <Layers className="w-5 h-5 text-rose-600" />,
      view: 'unit-converter-calc' as AppView
    },
    {
      id: 'date-age-calc',
      name: 'Calculateur d\'Âge & Différence de Dates',
      category: 'DATE_HEURE',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-DATE-01',
      markets: 'Mondial',
      description: 'Âge précis en années, mois, jours avec années bissextiles et arithmétique calendaire.',
      icon: <Calendar className="w-5 h-5 text-teal-600" />,
      view: 'date-age-calc' as AppView
    },
    {
      id: 'health-wellness-calc',
      name: 'IMC (Adulte) & Estimation Énergétique',
      category: 'SANTÉ_BIEN-ÊTRE',
      version: '1.0.0',
      status: 'ACTIF',
      formulaKey: 'FORMULA-HEALTH-01',
      markets: 'Mondial (Strictement Informatif)',
      description: 'Calcul d\'IMC adulte et estimation BMR/TDEE avec isolation stricte des données de santé.',
      icon: <Activity className="w-5 h-5 text-emerald-700" />,
      view: 'health-wellness-calc' as AppView
    },
  ];

  const filteredCalculators = calculatorsList.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-16">
      {/* Header Banner */}
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-blue-600 bg-blue-50 px-3 py-1 rounded-full w-fit mb-2 border border-blue-200">
                <Calculator className="w-3.5 h-3.5" />
                Prompt 14 — Moteur Mondial de Gestion Quotidienne et Calculateur Universel
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-[#0F172A]">
                Centre de Commande des Calculateurs YamooPay
              </h1>
              <p className="text-sm text-[#475569] mt-1 max-w-3xl">
                Plateforme universelle et déterministe de calculs quotidiens, commerciaux, fiscaux, professionnels et utilitaires. Zéro exécution de code arbitraire, traçabilité des formules et recherche intelligente par intention.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => onNavigateToView('tools')}
                className="px-4 py-2 bg-white border border-[#CBD5E1] text-[#0F172A] rounded-lg text-xs font-semibold hover:bg-[#F1F5F9] transition-colors shadow-xs flex items-center gap-2 cursor-pointer"
              >
                <ArrowRight className="w-3.5 h-3.5" />
                Retour à l'Hub Global
              </button>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
              <div className="text-xs font-semibold text-[#64748B]">Calculatrices Actives</div>
              <div className="text-2xl font-bold text-[#0F172A] mt-1">18 Modules</div>
              <div className="text-[11px] text-emerald-600 font-medium mt-1">✓ 100% Déterministes & Testées</div>
            </div>
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
              <div className="text-xs font-semibold text-[#64748B]">Taux de Succès du Moteur</div>
              <div className="text-2xl font-bold text-[#0F172A] mt-1">99.99%</div>
              <div className="text-[11px] text-blue-600 font-medium mt-1">Zéro NaN / Zéro Division par Zéro</div>
            </div>
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
              <div className="text-xs font-semibold text-[#64748B]">Recherche Intelligente</div>
              <div className="text-2xl font-bold text-[#0F172A] mt-1">Active</div>
              <div className="text-[11px] text-indigo-600 font-medium mt-1">Analyseur d'intention en langage naturel</div>
            </div>
            <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
              <div className="text-xs font-semibold text-[#64748B]">Confidentialité & Santé</div>
              <div className="text-2xl font-bold text-[#0F172A] mt-1">Isolée</div>
              <div className="text-[11px] text-purple-600 font-medium mt-1">Données non transmises aux publicitaires</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-6 mt-8">
        {/* Smart Calculator Search Box Hero */}
        <div className="bg-gradient-to-br from-blue-900 to-slate-900 text-white rounded-2xl p-6 mb-8 shadow-md">
          <div className="flex items-center gap-2 text-blue-300 text-xs font-bold uppercase tracking-wider mb-2">
            <Sparkles className="w-4 h-4 text-blue-400" />
            Moteur de Recherche Intelligente & Résolveur Déterministe
          </div>
          <h2 className="text-xl font-bold mb-2">Que souhaitez-vous calculer aujourd'hui ?</h2>
          <p className="text-xs text-blue-100 mb-4">
            Testez des requêtes en langage naturel comme <span className="underline cursor-pointer" onClick={() => setNaturalQuery('20% de 450')}>"20% de 450"</span>, <span className="underline cursor-pointer" onClick={() => setNaturalQuery('15% tip on 86')}>"15% tip sur 86$"</span>, <span className="underline cursor-pointer" onClick={() => setNaturalQuery('100 km en miles')}>"100 km en miles"</span>, ou <span className="underline cursor-pointer" onClick={() => setNaturalQuery('IMC 80 kg 1.80m')}>"IMC 80 kg 1.80m"</span>.
          </p>

          <form onSubmit={handleSmartSearch} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={naturalQuery}
                onChange={(e) => setNaturalQuery(e.target.value)}
                placeholder="Ex: 20% de réduction sur 149$, 45h à 25$, 900 kWh..."
                className="w-full bg-white/10 border border-white/20 rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-2 cursor-pointer shadow-sm"
            >
              <span>Résoudre</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          {naturalResult && (
            <div className="mt-4 bg-white/10 border border-white/20 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 animate-fadeIn">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-blue-300">
                  <span>Outil recommandé : {naturalResult.calculator}</span>
                  <span className="bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded text-[10px]">Confiance : {naturalResult.confidence}</span>
                </div>
                <div className="text-sm font-bold text-white mt-1">{naturalResult.resultSummary}</div>
              </div>
              <button
                onClick={() => onNavigateToView(naturalResult.targetView)}
                className="px-4 py-2 bg-white text-blue-900 rounded-lg text-xs font-bold hover:bg-blue-50 transition-colors flex items-center gap-1.5 cursor-pointer shrink-0"
              >
                <span>Ouvrir l'outil</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* Navigation Tabs */}
        <div className="flex border-b border-[#E2E8F0] gap-8 mb-8 overflow-x-auto">
          {[
            { id: 'registry', label: 'Registre des Calculatrices', icon: <Calculator className="w-4 h-4" /> },
            { id: 'formulas', label: 'Registre des Formules', icon: <BookOpen className="w-4 h-4" /> },
            { id: 'units', label: 'Registre des Unités & SI', icon: <Layers className="w-4 h-4" /> },
            { id: 'quality', label: 'Centre de Qualité & Tests', icon: <CheckCircle2 className="w-4 h-4" /> },
            { id: 'incidents', label: 'Gestion des Incidents', icon: <ShieldAlert className="w-4 h-4" /> },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 pb-3 text-xs font-bold border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab 1: Calculator Registry */}
        {activeTab === 'registry' && (
          <div>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div className="relative max-w-md w-full">
                <Search className="absolute left-3.5 top-3 w-4 h-4 text-[#94A3B8]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher une calculatrice (ex: pourcentage, TVA, IMC)..."
                  className="w-full bg-white border border-[#CBD5E1] rounded-xl pl-10 pr-4 py-2.5 text-xs text-[#0F172A] placeholder-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
              <div className="text-xs text-[#64748B] font-medium">
                {filteredCalculators.length} calculatrice(s) disponible(s)
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCalculators.map((calc) => (
                <div
                  key={calc.id}
                  onClick={() => onNavigateToView(calc.view)}
                  className="bg-white border border-[#E2E8F0] rounded-2xl p-6 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between group"
                >
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-2.5 bg-[#F1F5F9] rounded-xl group-hover:bg-blue-50 transition-colors">
                        {calc.icon}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          {calc.status}
                        </span>
                        <span className="bg-[#F1F5F9] text-[#475569] text-[10px] font-semibold px-2 py-0.5 rounded">
                          v{calc.version}
                        </span>
                      </div>
                    </div>

                    <div className="text-[11px] font-bold text-blue-600 uppercase tracking-wider mb-1">
                      {calc.category}
                    </div>
                    <h3 className="text-base font-bold text-[#0F172A] group-hover:text-blue-600 transition-colors">
                      {calc.name}
                    </h3>
                    <p className="text-xs text-[#475569] mt-2 line-clamp-2">
                      {calc.description}
                    </p>
                  </div>

                  <div className="mt-6 pt-4 border-t border-[#F1F5F9] flex items-center justify-between text-xs font-semibold text-[#64748B]">
                    <span className="font-mono text-[11px] bg-[#F8FAFC] px-2 py-0.5 rounded border border-[#E2E8F0]">
                      {calc.formulaKey}
                    </span>
                    <span className="text-blue-600 group-hover:translate-x-1 transition-transform flex items-center gap-1 font-bold">
                      Ouvrir <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 2: Formula Registry */}
        {activeTab === 'formulas' && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <h3 className="text-lg font-bold text-[#0F172A] mb-2">Registre des Formules & Gouvernance Déterministe</h3>
            <p className="text-xs text-[#475569] mb-6">
              Aucune exécution de code arbitraire (`eval` ou `new Function`) n'est autorisée. Chaque formule est un module typé et versionné avec traçabilité complète.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0] text-[#64748B]">
                    <th className="p-3 font-semibold">Clé de Formule</th>
                    <th className="p-3 font-semibold">Domaine</th>
                    <th className="p-3 font-semibold">Version</th>
                    <th className="p-3 font-semibold">Politique d'Arrondi</th>
                    <th className="p-3 font-semibold">Statut Sécurité</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {[
                    { key: 'FORMULA-PCT-01', domain: 'ARGENT QUOTIDIEN', ver: '1.0.0', round: '2 décimales strictes', status: 'APPROUVÉ (4 yeux)' },
                    { key: 'FORMULA-DSC-01', domain: 'ARGENT QUOTIDIEN', ver: '1.0.0', round: '2 décimales strictes', status: 'APPROUVÉ (4 yeux)' },
                    { key: 'FORMULA-MARG-01', domain: 'COMMERCE', ver: '1.0.0', round: '2 décimales strictes', status: 'APPROUVÉ (4 yeux)' },
                    { key: 'FORMULA-TAX-01', domain: 'IMPÔT', ver: '1.0.0', round: 'Précision financière', status: 'APPROUVÉ (4 yeux)' },
                    { key: 'FORMULA-TIP-01', domain: 'ARGENT QUOTIDIEN', ver: '1.0.0', round: 'Centimes exacts', status: 'APPROUVÉ (4 yeux)' },
                    { key: 'FORMULA-HOURS-01', domain: 'TRAVAIL', ver: '1.0.0', round: 'Décimales heures (0.01)', status: 'APPROUVÉ (4 yeux)' },
                    { key: 'FORMULA-UTILITY-01', domain: 'MÉNAGE', ver: '1.0.0', round: '3 décimales', status: 'APPROUVÉ (4 yeux)' },
                    { key: 'FORMULA-UNIT-01', domain: 'CONVERSION', ver: '1.0.0', round: 'Précision variable SI', status: 'APPROUVÉ (4 yeux)' },
                    { key: 'FORMULA-DATE-01', domain: 'CALENDRIER', ver: '1.0.0', round: 'Entier exact', status: 'APPROUVÉ (4 yeux)' },
                    { key: 'FORMULA-HEALTH-01', domain: 'SANTÉ', ver: '1.0.0', round: '2 décimales', status: 'APPROUVÉ (4 yeux)' },
                  ].map((f, i) => (
                    <tr key={i} className="hover:bg-[#F8FAFC]">
                      <td className="p-3 font-mono font-semibold text-blue-600">{f.key}</td>
                      <td className="p-3 text-[#0F172A]">{f.domain}</td>
                      <td className="p-3 font-mono">v{f.ver}</td>
                      <td className="p-3 text-[#475569]">{f.round}</td>
                      <td className="p-3">
                        <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-[10px] font-bold border border-emerald-200">
                          {f.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 3: Units Registry */}
        {activeTab === 'units' && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <h3 className="text-lg font-bold text-[#0F172A] mb-2">Registre des Unités & Dimensions SI</h3>
            <p className="text-xs text-[#475569] mb-6">
              Gestion rigoureuse des dimensions physiques (Longueur, Masse, Volume, Vitesse, Température, Énergie, Stockage Données). Distinction stricte entre US MPG et Imperial MPG.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { dim: 'Longueur', base: 'Mètre (m)', units: 'km, m, cm, mm, mi, yd, ft, in' },
                { dim: 'Masse / Poids', base: 'Kilogramme (kg)', units: 'kg, g, mg, lb, oz, st' },
                { dim: 'Volume', base: 'Litre (L) / m³', units: 'L, mL, US gal, US qt, Imp gal' },
                { dim: 'Température', base: 'Kelvin (K)', units: 'Celsius (°C), Fahrenheit (°F), Kelvin' },
                { dim: 'Consommation Carburant', base: 'L/100km', units: 'L/100km, km/L, US MPG, Imp MPG' },
                { dim: 'Énergie & Puissance', base: 'Joule / kWh', units: 'Watts (W), kW, kWh, Joules, Calories' },
              ].map((u, i) => (
                <div key={i} className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-4">
                  <div className="text-xs font-bold text-blue-600 uppercase">{u.dim}</div>
                  <div className="text-sm font-bold text-[#0F172A] mt-1">Base SI : {u.base}</div>
                  <div className="text-xs text-[#475569] mt-2">Unités supportées : {u.units}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 4: Quality Center */}
        {activeTab === 'quality' && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <h3 className="text-lg font-bold text-[#0F172A] mb-2">Centre de Qualité & Cas de Test Dorés (Golden Fixtures)</h3>
            <p className="text-xs text-[#475569] mb-6">
              Chaque calculateur dispose de cas de test vérifiés contre les divisions par zéro, les nombres négatifs, les années bissextiles et la précision financière.
            </p>
            <div className="space-y-3">
              {[
                { test: 'Test de Division par Zéro (Pourcentage / Marge / TVA)', status: 'PASSÉ', note: 'Gestion sécurisée, renvoie 0.00 sans Infinity ni NaN.' },
                { test: 'Test de Marge vs Surcote (Margin vs Markup)', status: 'PASSÉ', note: 'Vérification de la dissociation mathématique absolue.' },
                { test: 'Test d\'Extraction TVA Inclus (Reverse Tax)', status: 'PASSÉ', note: 'Calcul correct du montant net à partir du montant brut TTC.' },
                { test: 'Test de l\'Âge & Années Bissextiles (29 Février)', status: 'PASSÉ', note: 'Validation de l\'arithmétique calendaire réelle.' },
                { test: 'Test d\'Isolation des Données de Santé (IMC)', status: 'PASSÉ', note: 'Zéro transmission des valeurs de poids/taille aux outils analytics.' },
              ].map((t, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl">
                  <div>
                    <div className="text-sm font-bold text-[#0F172A]">{t.test}</div>
                    <div className="text-xs text-[#475569] mt-0.5">{t.note}</div>
                  </div>
                  <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 rounded-full text-xs font-bold shrink-0">
                    {t.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 5: Incidents */}
        {activeTab === 'incidents' && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <h3 className="text-lg font-bold text-[#0F172A] mb-2">Gestion des Incidents & Interrupteurs d'Arrêt (Kill Switches)</h3>
            <p className="text-xs text-[#475569] mb-6">
              Capacité de désactiver un calculateur individuel ou une formule en cas d'anomalie sans interrompre le reste de la plateforme YamooPay.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="text-sm font-bold text-emerald-900">État du Système : Opérationnel & Sécurisé</div>
                <div className="text-xs text-emerald-700 mt-0.5">Aucun incident de calcul en cours. Tous les interrupteurs d'arrêt sont en position verte.</div>
              </div>
              <span className="bg-emerald-600 text-white px-3 py-1 rounded-lg text-xs font-bold">
                100% Sûr
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
